import { booleanAttribute, ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, UntypedFormGroup, ValidatorFn } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { SelectionItem } from '../../../core/component/select/select.component';
import { getMonthName } from '../../../core/helper/month';
import { LocalDate } from '../../../core/model/local-date';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { LanguageService, SWEDISH_CULTURE } from '../../../core/service/language.service';
import { LettingAdvertisment } from '../../model/letting-advertisment';
import { HomeqOptions } from '../../model/platform-options';

@Component({
  selector: 'letting-advertisment-homeq',
  templateUrl: 'letting-advertisment-homeq.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LettingAdvertismentHomeqComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) letting: LettingAdvertisment;
  @Input({ required: true }) homeqOptions: HomeqOptions;
  @Input({ required: true }) officeName: string;
  @Input({ transform: booleanAttribute }) readonly: boolean = false;

  public form: UntypedFormGroup;

  protected monthSelection: SelectionItem[] = Array(12)
    .fill(undefined)
    .map((_, i) => new SelectionItem(getMonthName(i + 1), i + 1));

  private destroy = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly formFactory: FormControlExFactory,
    private readonly languageService: LanguageService
  ) {}

  ngOnInit(): void {
    const objectDescription =
      this.letting.objectDescription.find(x => x.cultureName === SWEDISH_CULTURE)?.translationText ||
      this.letting.objectDescription.find(x => x.translationText)?.translationText;

    this.form = this.formBuilder.group({
      homeqRequirementProfile: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.HOMEQ.REQUIREMENT_PROFILE',
        this.letting.homeqRequirementProfile,
        { isReadonly: this.readonly }
      ),
      homeqIsNewProduction: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.HOMEQ.NEW_PRODUCTION',
        { value: this.letting.homeqIsNewProduction, disabled: this.readonly },
        {
          isReadonly: this.readonly,
        }
      ),
      vacancyFrom: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.VACANT_FROM', this.letting.vacancyFrom, {
        required: true,
        isReadonly: this.readonly,
        validator: this.emptyLocalDateValidator,
      }),
      vacancyTo: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.VACANT_TO', this.letting.vacancyTo, {
        isReadonly: this.readonly,
      }),
      publishFrom: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.PUBLISH_FROM', LocalDate.fromDate(this.letting.publishFrom), {
        required: true,
        isReadonly: this.readonly,
        validator: this.emptyLocalDateValidator,
      }),
      publishTo: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.PUBLISH_TO', LocalDate.fromDate(this.letting.publishTo), {
        isReadonly: this.readonly,
      }),
      monthlyRent: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.MONTHLY_RENT', this.letting.monthlyRent, {
        required: true,
        isReadonly: this.readonly,
      }),
      yearlyRent: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.YEARLY_RENT', this.letting.yearlyRent, {
        required: true,
        isReadonly: this.readonly,
      }),
      monthsWithRent: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.MONTHS_WITH_RENT', this.letting.monthsWithRent, {
        isReadonly: this.readonly,
      }),
      objectDescription: this.formFactory.createHidden(this.letting.objectDescription),
      homeQObjectDescription: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.OBJECT_DESCRIPTION', objectDescription, {
        isReadonly: this.readonly,
        validator: this.descriptionValidator,
      }),
      uniqueObjectDescription: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DONT_UPDATE_DESCRIPTION',
        { value: this.letting.uniqueObjectDescription, disabled: this.readonly },
        { isReadonly: this.readonly, helpText: 'LETTING.ADVERTISMENT.HELP_TEXT.DONT_UPDATE_DESCRIPTION_OBJECT' }
      ),
      buildingDescription: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.BUILDING_DESCRIPTION',
        this.letting.buildingDescription,
        {
          isReadonly: this.readonly,
          validator: this.descriptionValidator,
        }
      ),
      uniqueBuildingDescription: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DONT_UPDATE_DESCRIPTION',
        { value: this.letting.uniqueBuildingDescription, disabled: this.readonly },
        { isReadonly: this.readonly, helpText: 'LETTING.ADVERTISMENT.HELP_TEXT.DONT_UPDATE_DESCRIPTION_BUILDING' }
      ),
      buildingAreaDescription: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.AREA_DESCRIPTION',
        this.letting.buildingAreaDescription,
        {
          isReadonly: this.readonly,
          validator: this.descriptionValidator,
        }
      ),
      uniqueBuildingAreaDescription: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DONT_UPDATE_DESCRIPTION',
        { value: this.letting.uniqueBuildingAreaDescription, disabled: this.readonly },
        { isReadonly: this.readonly, helpText: 'LETTING.ADVERTISMENT.HELP_TEXT.DONT_UPDATE_DESCRIPTION_AREA' }
      ),
    });

    if (!this.letting.id) {
      this.form.controls['homeqRequirementProfile'].setValue(
        this.homeqOptions.requirementProfiles.length > 0 ? this.homeqOptions.requirementProfiles[0].value : null
      );
    }

    this.form.controls.homeQObjectDescription.valueChanges.pipe(takeUntil(this.destroy)).subscribe({
      next: value => {
        this.form.controls.objectDescription.setValue([
          ...this.letting.objectDescription.filter(x => x.cultureName !== SWEDISH_CULTURE),
          { cultureName: SWEDISH_CULTURE, translationText: value },
        ]);
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.readonly && this.form) {
      this.applyReadonly(this.form);
    }
  }

  private emptyLocalDateValidator: ValidatorFn = (control: AbstractControl<LocalDate>): { [key: string]: any } | null =>
    control.value?.year !== null && control.value?.month !== null && control.value?.day !== null ? null : { required: 'required' };

  private descriptionValidator: ValidatorFn = (c: AbstractControl) => {
    if (!c.value) {
      return null;
    }
    let message: string = null;
    if (c.value.length < 100) {
      message = this.languageService.getText('ERROR.MIN_LENGTH').replace('{{min}}', '100');
    }
    if (c.value.length > 3000) {
      message = this.languageService
        .getText('ERROR.MAX_LENGTH')
        .replace('{{requiredLength}}', '3000')
        .replace('{{actualLength}}', c.value.length.toString());
    }

    return message
      ? {
          error: {
            message: 'ERROR.FORMAT_PRM',
            params: { 0: message },
          },
        }
      : null;
  };

  private applyReadonly(control: AbstractControl): void {
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.keys(control.controls).forEach(key => this.applyReadonly(control.controls[key]));
      return;
    }

    if (control instanceof FormControlEx) {
      control.isReadonly = this.readonly;
    }

    control.disable();
    control.updateValueAndValidity({ emitEvent: false });
  }
}
